import type { TrainRenderData } from '@/types';

import { SCATTER_RADIUS, SCATTER_START_ANGLE } from '@/utils/constants/map';

export const scatterStoppedTrains = (
  stoppedGroups: Map<number, number[]>,
  pool: TrainRenderData[]
) => {
  for (const indices of stoppedGroups.values()) {
    const count = indices.length;

    if (count === 0) {
      continue;
    }

    const baseIndex = indices[0];
    const baseItem = pool[baseIndex];

    if (!baseItem) {
      continue;
    }

    const [cx, cy] = baseItem.position;

    const latRad = (cy * Math.PI) / 180;
    const lonCorrection = 1 / Math.cos(latRad);

    // --- CASE 1: EXACTLY 1 TRAIN ---
    if (count === 1) {
      const item = pool[indices[0]];

      if (item) {
        // Jump to the left of its current bearing
        const angleDeg = item.bearing - SCATTER_START_ANGLE;
        const angleRad = (angleDeg * Math.PI) / 180;

        item.position[0] = cx + SCATTER_RADIUS * Math.sin(angleRad) * lonCorrection;
        item.position[1] = cy + SCATTER_RADIUS * Math.cos(angleRad);
      }
      continue;
    }

    // --- CASE 2: EXACTLY 2 TRAINS ---
    if (count === 2) {
      const item0 = pool[indices[0]];
      const item1 = pool[indices[1]];

      if (item0 && item1) {
        // Train 0 always jumps to its left
        const angleDeg0 = item0.bearing - SCATTER_START_ANGLE;
        const angleRad0 = (angleDeg0 * Math.PI) / 180;
        item0.position[0] = cx + SCATTER_RADIUS * Math.sin(angleRad0) * lonCorrection;
        item0.position[1] = cy + SCATTER_RADIUS * Math.cos(angleRad0);

        // Figure out if Train 1 is going the exact same way as Train 0 (within ~45 degrees)
        const diff = Math.abs((item0.bearing - item1.bearing + 360) % 360);
        const isSameDirection = diff < 45 || diff > 315;

        // If same direction, Train 1 goes Right (+). If opposite, Train 1 goes Left (-).
        const angleDeg1 = isSameDirection
          ? item1.bearing + SCATTER_START_ANGLE
          : item1.bearing - SCATTER_START_ANGLE;

        const angleRad1 = (angleDeg1 * Math.PI) / 180;
        item1.position[0] = cx + SCATTER_RADIUS * Math.sin(angleRad1) * lonCorrection;
        item1.position[1] = cy + SCATTER_RADIUS * Math.cos(angleRad1);
      }
      continue;
    }

    // --- CASE 3: 3 OR MORE TRAINS (Scatter Circle) ---
    const baseBearing = baseItem.bearing;
    const angleStep = 360 / count;

    for (let k = 0; k < count; k++) {
      const idx = indices[k];
      const item = pool[idx];

      if (!item) {
        continue;
      }

      const angleDeg = baseBearing - SCATTER_START_ANGLE + k * angleStep;
      const angleRad = (angleDeg * Math.PI) / 180;

      const dLon = SCATTER_RADIUS * Math.sin(angleRad) * lonCorrection;
      const dLat = SCATTER_RADIUS * Math.cos(angleRad);

      item.position[0] = cx + dLon;
      item.position[1] = cy + dLat;
    }
  }
};
